import { BASE_URL } from '~/service/constants'
import {
  apiActiveSubscriptionPath,
  apiFetchInvoicesPath,
  apiFetchSubscriptionEstimationPath,
  apiSubscriptionPlansPath,
  apiSubscriptionsPath,
  apiUpdateCompanyPath,
  apiDeletePaymentMethodPath,
  apiSetupIntentPath,
  apiPaymentMethodsPath,
  apiUpdatePaymentMethodPath,
  apiUpdateSubscriptionSeatsPath,
} from '~/routes'

const headers = (token, csrf = null, isFormData = false) => {
  const baseHeaders = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  }

  if (!isFormData) baseHeaders['Content-Type'] = 'application/json'
  if (csrf) baseHeaders['X-CSRF-Token'] = csrf

  return baseHeaders
}

const handleApiError = async (response, defaultErrorMessage = '') => {
  let errorData
  try {
    errorData = await response.json()
  } catch (parseError) {
    throw new Error(`${defaultErrorMessage} (HTTP ${response.status})`)
  }

  if (
    errorData.error &&
    typeof errorData.error === 'string' &&
    !Array.isArray(errorData?.notifications)
  ) {
    throw new Error(errorData.error)
  }
  if (errorData && errorData.errors && errorData.errors.length > 0) {
    throw new Error(errorData?.errors?.[0] || defaultErrorMessage)
  }
  throw new Error(defaultErrorMessage)
}

export async function fetchActiveSubscription(companyId, jwt) {
  const response = await fetch(
    `${BASE_URL}${apiActiveSubscriptionPath(companyId)}`,
    { headers: headers(jwt) }
  )
  if (!response.ok) throw new Error('Failed to fetch subscription')
  return response.json()
}

export async function fetchPlans(companyId, jwt, quantity, signal) {
  const params = quantity != null ? `?${new URLSearchParams({ quantity })}` : ''
  const response = await fetch(
    `${BASE_URL}${apiSubscriptionPlansPath(companyId)}${params}`,
    { headers: headers(jwt), signal }
  )
  if (!response.ok) throw new Error('Failed to fetch plans')
  const data = await response.json()
  return data.plans ?? []
}

export async function fetchEstimate(companyId, jwt, unit, quantity, signal, { mode } = {}) {
  const params = new URLSearchParams({ unit, quantity })
  if (mode) params.append('mode', mode)
  const response = await fetch(
    `${BASE_URL}${apiFetchSubscriptionEstimationPath(companyId)}?${params}`,
    { headers: headers(jwt), signal }
  )
  if (!response.ok) await handleApiError(response, 'Failed to fetch estimate')
  return response.json()
}

// Attribution for an upgrade started from an announcement; the server verifies the token
// (Announcements::RecordConversion). Omitted when no announcement is behind the upgrade.
const announcementAttribution = (announcementRenderToken) => (
  announcementRenderToken
    ? {
      announcement_render_token: announcementRenderToken,
      page_path: typeof window === 'undefined' ? null : window.location.pathname,
    }
    : {}
)

export async function createSubscription(companyId, jwt, csrf, unit, quantity, { announcementRenderToken } = {}) {
  const response = await fetch(
    `${BASE_URL}${apiSubscriptionsPath(companyId)}`,
    {
      method: 'POST',
      headers: headers(jwt, csrf),
      body: JSON.stringify({
        subscription: { unit, quantity },
        ...announcementAttribution(announcementRenderToken),
      }),
    }
  )
  if (!response.ok) await handleApiError(response, 'Failed to create subscription')
  return response.json()
}

export async function updateSubscription(companyId, subscriptionId, jwt, csrf, unit, quantity, mode = null, { announcementRenderToken } = {}) {
  const subscriptionData = { unit, quantity }
  if (mode) subscriptionData.mode = mode
  const response = await fetch(
    `${BASE_URL}${apiUpdateSubscriptionSeatsPath(companyId, subscriptionId)}`,
    {
      method: 'PATCH',
      headers: headers(jwt, csrf),
      body: JSON.stringify({
        subscription: subscriptionData,
        ...announcementAttribution(announcementRenderToken),
      }),
    }
  )
  if (!response.ok) await handleApiError(response, 'Failed to update subscription')
  return response.json()
}

export const fetchInvoices = async (companyId, jwt, csrf, page, perPage) => {
  const queryParams = new URLSearchParams({ page, page_size: perPage }).toString()
  const response = await fetch(`${BASE_URL}${apiFetchInvoicesPath(companyId)}?${queryParams}`, {
    method: 'GET',
    headers: headers(jwt, csrf),
  })
  if (!response.ok) {
    await handleApiError(response, 'Failed to fetch invoices')
  }

  return response.json()
}

export const updateCompany = async (companyId, jwt, csrf, data) => {
  const response = await fetch(`${BASE_URL}${apiUpdateCompanyPath(companyId)}`, {
    method: 'PATCH',
    headers: headers(jwt, csrf),
    body: JSON.stringify(data)
  })
  if (!response.ok) {
    await handleApiError(response, 'Failed to update company')
  }

  return response.json()
}

export const deletePaymentMethod = async (companyId, paymentMethodId, jwt, csrf) => {
  const response = await fetch(`${BASE_URL}${apiDeletePaymentMethodPath(companyId, paymentMethodId)}`, {
    method: 'DELETE',
    headers: headers(jwt, csrf),
  })
  if (!response.ok) {
    await handleApiError(response, 'Failed to delete payment method')
  }

  if (response.status === 204) return null
  return response.json()
}

export const fetchPaymentMethods = async (companyId, jwt, csrf) => {
  const response = await fetch(`${BASE_URL}${apiPaymentMethodsPath(companyId)}`, {
    method: 'GET',
    headers: headers(jwt, csrf),
  })
  if (!response.ok) {
    await handleApiError(response, 'Failed to fetch payment methods')
  }
  return response.json()
}

export const fetchSetupIntent = async (companyId, jwt, csrf) => {
  const response = await fetch(`${BASE_URL}${apiSetupIntentPath(companyId)}`, {
    method: 'GET',
    headers: headers(jwt, csrf),
  })
  if (!response.ok) {
    await handleApiError(response, 'Failed to create setup intent')
  }
  return response.json()
}

export const createPaymentMethod = async (companyId, stripePaymentMethodId, jwt, csrf) => {
  const response = await fetch(`${BASE_URL}${apiPaymentMethodsPath(companyId)}`, {
    method: 'POST',
    headers: headers(jwt, csrf),
    body: JSON.stringify({ payment_method: { stripe_payment_method_id: stripePaymentMethodId } }),
  })
  if (!response.ok) {
    await handleApiError(response, 'Failed to save payment method')
  }
  return response.json()
}

export const sendKnowledgeBaseMessage = async (companyId, jwt, csrf, subject, message) => {
  const response = await fetch(`${BASE_URL}/api/companies/${companyId}/knowledge_base_messages`, {
    method: 'POST',
    headers: headers(jwt, csrf),
    body: JSON.stringify({ subject, message }),
  })
  if (!response.ok) {
    await handleApiError(response, 'Failed to send message')
  }
  return response.json()
}

export const updatePrimaryPaymentMethod = async (companyId, paymentMethodId, jwt, csrf) => {
  const response = await fetch(`${BASE_URL}${apiUpdatePaymentMethodPath(companyId, paymentMethodId)}`, {
    method: 'PATCH',
    headers: headers(jwt, csrf),
  })
  if (!response.ok) {
    await handleApiError(response, 'Failed to update primary payment method')
  }

  return response.json()
}

export const updateSubscriptionSeats = async (companyId, subscriptionId, jwt, csrf, data) => {
  const response = await fetch(`${BASE_URL}${apiUpdateSubscriptionSeatsPath(companyId, subscriptionId)}`, {
    method: 'PATCH',
    headers: headers(jwt, csrf),
    body: JSON.stringify(data)
  })
  if (!response.ok) {
    await handleApiError(response, 'Failed to update subscription seats')
  }

  return response.json()
}
